import { cva, type VariantProps } from 'class-variance-authority';

import { cn, Flex, Icon, type IconType } from '@kognitos/lattice';

import { EStepType } from '../types';

const stepTypeIndicatorVariants = cva('p-0.5', {
  variants: {
    type: {
      [EStepType.GOAL]: 'text-muted-foreground',
      [EStepType.NAVIGATE]: 'text-muted-foreground',
      [EStepType.CLICK]: 'text-muted-foreground',
      [EStepType.TYPE]: 'text-muted-foreground',
      [EStepType.THOUGHT]: 'text-muted-foreground',
      [EStepType.OUTPUT]: 'text-success',
    },
    isCurrent: {
      true: 'text-primary',
      false: '',
    },
  },
  defaultVariants: {
    type: EStepType.GOAL,
    isCurrent: false,
  },
});

/**
 * Map of step types to their corresponding icon types.
 */
const stepTypeIconMap: Record<EStepType, IconType> = {
  [EStepType.GOAL]: 'Crosshair',
  [EStepType.NAVIGATE]: 'Navigation',
  [EStepType.CLICK]: 'MousePointerClick',
  [EStepType.TYPE]: 'TextCursorInput',
  [EStepType.THOUGHT]: 'MessageCircle',
  [EStepType.OUTPUT]: 'CircleCheck',
};

/**
 * Props for the StepTypeIndicator component.
 */
export interface IStepTypeIndicatorProps
  extends VariantProps<typeof stepTypeIndicatorVariants> {
  /** The type of step to display. */
  type?: EStepType;
  /** Whether this indicator represents the current step (tints it the brand color). */
  isCurrent?: boolean;
  /** Additional CSS classes. */
  className?: string;
}

/**
 * StepTypeIndicator displays an icon representing the type of browser automation step.
 *
 * @param props - Component props
 * @returns StepTypeIndicator component
 *
 * @example
 * <StepTypeIndicator type={EStepType.GOAL} />
 * <StepTypeIndicator type={EStepType.CLICK} isCurrent />
 */
export function StepTypeIndicator({
  type = EStepType.GOAL,
  isCurrent = false,
  className,
}: IStepTypeIndicatorProps) {
  const iconType = stepTypeIconMap[type];

  return (
    <Flex
      align="center"
      className={cn(
        stepTypeIndicatorVariants({
          type: type,
          isCurrent,
        }),
        className,
      )}
    >
      <Icon type={iconType} size="sm" />
    </Flex>
  );
}

export { stepTypeIndicatorVariants };
